import { useState, useEffect } from 'react';
import { preloadImage, loadExportLibrary } from '@utils/common';
import { getAspectRatio } from './util.get-aspect-ratio.js';
import { shouldOpenInLightbox } from './util.should-open-in-lightbox.js';

function usePreviewImage(args, onPreviewImageLoaded) {
  const [previewImage, setImage] = useState();
  const [isGExportImage, setIsGExportImage] = useState();
  const [didError, setDidError] = useState(false);
  useEffect(() => {
    // If the preview-image already has bene resolved, then do not bother
    // re-fetching it.
    if (previewImage) {
      return;
    }
    const fetchPreview = async () => {
      // Do not bother attempting to load a preview image if one has already
      // been predefined or if the iframe src isn't ready yet.
      if (!args.displayData || args.previewImageSrc || args.isRenderingIframe) {
        return;
      }
      await loadExportLibrary();
      if (typeof window.gExport === "undefined") {
        throw new Error("<Iframe />.usePreviewImage(): gExport is not available, bailing out.");
      }
      const preset = window.devicePixelRatio >= 2 ? "hd" : "sd";
      const aspect = shouldOpenInLightbox({
        componentRef: args.componentRef,
        displayData: args.displayData,
        forceOpenInLightbox: args.forceOpenInLightbox
      }) ? 0.5625 : getAspectRatio({
        displayData: args.displayData
      });

      // Generate the url we'd like to use when generating the desired
      // screenshot, stripping any sort of userID from it to avoid
      // accidentally sharing private data with other uses due to
      // caching
      const url = args.displayData.src.replace(/(userID)=[^&]+/gi, "$1={userId}").replace(/\{userId\}/g, "guest");
      const screenshot = await window.gExport.convert({
        aspectRatio: aspect,
        format: "jpg",
        input: window.gProxy.forwardTo(url),
        preset,
        // Cache screengrab for up to 1 week
        cache: 7 * 24 * 60 * 60 * 1000,
        // Generate screenshot using iframe embed-mode (as that's exactly
        // how the url will be embedded in this client, it will provide a
        // more realistic output)
        embed: true
      });
      return screenshot.url;
    };
    const runEffect = async () => {
      try {
        let src = args.previewImageSrc;
        let isGExportImage = false;

        // In case a previewImage is available, then we can preload and use that
        // immediately...
        if (!src) {
          src = await fetchPreview();
          isGExportImage = true;
          if (!src) {
            // In case a preview image still isn't available, then it is most
            // likely due to props not being ready yet.. Bail out for now.
            return;
          }
        }
        const img = await preloadImage(src);
        setImage(src);
        setIsGExportImage(isGExportImage);
        setDidError(false);
        onPreviewImageLoaded?.(img, isGExportImage);
      } catch (err) {
        console.warn(err);
        setDidError(true);
      }
    };
    runEffect();
  }, [onPreviewImageLoaded, args.displayData, args.previewImageSrc, args.isRenderingIframe, args.componentRef, args.forceOpenInLightbox, previewImage]);
  return [previewImage, isGExportImage, didError];
}

export { usePreviewImage };
//# sourceMappingURL=hook.preview-image.js.map
